import { conform, useForm } from '@conform-to/react'
import { getFieldsetConstraint, parse } from '@conform-to/zod'
import * as E from '@react-email/components'
import {
	json,
	redirect,
	type DataFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { Form, useActionData } from '@remix-run/react'
import * as React from 'react'
import { ClientOnly } from 'remix-utils/client-only'
import { z } from 'zod'
import { Captcha } from '#app/components/captcha.client.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList, ErrorListAlert, Field } from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { checkCaptcha } from '#app/utils/captcha.server.ts'
import {
	ProviderConnectionForm,
	showProviderNames,
} from '#app/utils/connections.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { validateEmail } from '#app/utils/email-validation.ts'
import { sendEmail } from '#app/utils/email.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { redditTrack } from '#app/utils/reddit-pixel.client.tsx'
import { EmailSchema } from '#app/utils/user-validation.ts'
import { prepareVerification } from './verify.tsx'

const SignupSchema = z.object({
	email: EmailSchema,
})

function getEmailWithoutPlus(email: string) {
	let chunks = email.split('@')
	let emailWithousPlus = chunks[0].split('+')[0] + '@' + chunks[1]
	return emailWithousPlus.toLowerCase()
}

async function emailIsDuplicated(email: string) {
	// check email in the so the + trick doesn't work
	let emailWithousPlus = getEmailWithoutPlus(email)
	let firstPart = emailWithousPlus.split('@')[0]
	let users = await prisma.user.findMany({
		select: { email: true },
		where: {
			email: { startsWith: firstPart },
		},
	})
	let dbEmails = users.map(user => getEmailWithoutPlus(user.email))
	return dbEmails.includes(emailWithousPlus)
}

export async function action({ request }: DataFunctionArgs) {
	const formData = await request.formData()
	if (!(await checkCaptcha(request, formData))) {
		return json(
			{
				status: 'error',
				submission: null,
				message: 'The captcha verification failed',
			} as const,
			{ status: 400 },
		)
	}

	let email: string = formData.get('email') as string
	if (!(await validateEmail(email))) {
		return json(
			{
				status: 'error',
				submission: null,
				message:
					"Please enter a different email, we currently don't allow these emails.",
			} as const,
			{ status: 400 },
		)
	}

	if (await emailIsDuplicated(email)) {
		return json(
			{
				status: 'error',
				submission: null,
				message: 'A user already exists with this email',
			} as const,
			{ status: 400 },
		)
	}

	const submission = await parse(formData, {
		schema: SignupSchema.superRefine(async (data, ctx) => {
			const existingUser = await prisma.user.findUnique({
				where: { email: data.email },
				select: { id: true },
			})
			if (existingUser) {
				ctx.addIssue({
					path: ['email'],
					code: z.ZodIssueCode.custom,
					message: 'A user already exists with this email',
				})
				return
			}
		}),
		async: true,
	})
	if (submission.intent !== 'submit') {
		return json({ status: 'idle', submission } as const)
	}
	if (!submission.value) {
		return json({ status: 'error', submission } as const, { status: 400 })
	}
	const { verifyUrl, redirectTo, otp } = await prepareVerification({
		period: 10 * 60,
		request,
		type: 'onboarding',
		target: email,
	})

	const response = await sendEmail({
		to: email,
		subject: `Welcome to SwapMyFace!`,
		react: <SignupEmail onboardingUrl={verifyUrl.toString()} otp={otp} />,
	})

	if (response.status === 'success') {
		return redirect(redirectTo.toString())
	} else {
		submission.error[''] = [response.error.message]
		return json({ status: 'error', submission, message: '' } as const, {
			status: 500,
		})
	}
}

export function SignupEmail({
	onboardingUrl,
	otp,
}: {
	onboardingUrl: string
	otp: string
}) {
	return (
		<E.Html lang="en" dir="ltr">
			<E.Container>
				<h1>
					<E.Text>Welcome to SwapMyFace!</E.Text>
				</h1>
				<p>
					<E.Text>
						Here's your verification code: <strong>{otp}</strong>
					</E.Text>
				</p>
				<p>
					<E.Text>Or click the link to get started:</E.Text>
				</p>
				<E.Link href={onboardingUrl}>{onboardingUrl}</E.Link>
			</E.Container>
		</E.Html>
	)
}

export const meta: MetaFunction = () => {
	return [{ title: 'Sign Up | SwapMyFace' }]
}

export default function SignupRoute() {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()
	const [timestamp, setTimestamp] = React.useState(Date.now())
	// const [searchParams] = useSearchParams()
	// const redirectTo = searchParams.get('redirectTo')

	const [form, fields] = useForm({
		id: 'signup-form',
		constraint: getFieldsetConstraint(SignupSchema),
		lastSubmission: actionData?.submission ?? undefined,
		onValidate({ formData }) {
			const result = parse(formData, { schema: SignupSchema })
			return result
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="container flex flex-col justify-center pb-32 pt-20">
			<div className="text-center">
				<h1 className="text-h1">Let's start swapping faces!</h1>
				<p className="mt-3 text-body-md text-muted-foreground">
					Please enter your email.
				</p>
			</div>
			<div className="mx-auto mt-16 min-w-[368px] max-w-sm">
				{actionData?.status === 'error' && actionData?.submission === null ? (
					<div className="mx-auto max-w-sm py-4 ">
						<ErrorListAlert
							errors={actionData.message ? [actionData.message] : []}
							elementClassName="text-md"
						/>
					</div>
				) : null}
				<Form
					method="POST"
					{...form.props}
					onSubmit={() => {
						redditTrack('SignUp')
						setTimestamp(Date.now())
					}}
				>
					<Field
						labelProps={{
							htmlFor: fields.email.id,
							children: 'Email',
						}}
						inputProps={{ ...conform.input(fields.email), autoFocus: true }}
						errors={fields.email.errors}
					/>
					<ErrorList errors={form.errors} id={form.errorId} />
					<StatusButton
						className="w-full"
						status={isPending ? 'pending' : actionData?.status ?? 'idle'}
						type="submit"
						disabled={isPending}
					>
						Submit
					</StatusButton>
					<div className="pb-4 pt-5">
						<ClientOnly>
							{() => (
								<div>
									<Captcha rerender={timestamp} />
								</div>
							)}
						</ClientOnly>
					</div>
				</Form>
				<div className="mt-5 flex flex-col gap-5 border-b-2 border-t-2 border-border py-3">
					{showProviderNames.map(providerName => (
						<ProviderConnectionForm
							key={providerName}
							type="Signup"
							providerName={providerName}
							redirectTo="/me"
						/>
					))}
				</div>
			</div>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
